import React from "react";
import { Card } from "./ui.jsx";
import { LotIdentity } from "./lot_identity.jsx";
import { boxKey, boxLabel, useBoxAllocation } from "./box_allocation.js";

// The reviewed range arrives in box order; contiguous numbers are shown as one
// barn row, which is how a vendor walking the barn reads them.
function runs(boxes) {
  const grouped = [];
  for (const box of boxes) {
    const last = grouped[grouped.length - 1];
    if (last && last.barn === box.barn && box.number === last.to + 1) {
      last.to = box.number;
      last.items.push(box);
    } else {
      grouped.push({ barn: box.barn, from: box.number, to: box.number, items: [box] });
    }
  }
  return grouped;
}

// Each panel is keyed by sale/vendor at its call site. Pending work never crosses
// that boundary; no optimistic assignment or browser-persisted allocation exists.
export function BoxAssignments({ sale = "26R2R", vendor = window.VD.access.realm, admin = false }) {
  const allocation = useBoxAllocation({ sale, vendor, admin });
  const { data, error, notice, loading, busy, pending, locked, occupants, placeable, boxes } = allocation;
  const [open, setOpen] = React.useState("");
  const gridRef = React.useRef(null);
  const openedRef = React.useRef("");

  // Opening a box hands the keyboard to its list, and closing gives it back to
  // the box, so the whole exchange can be driven without a pointer.
  React.useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    if (open) {
      openedRef.current = open;
      const dialog = grid.querySelector('[role="dialog"]');
      const fit = () => {
        if (!dialog) return;
        dialog.style.marginLeft = "0px";
        if (window.innerWidth <= 640) return;
        const edge = dialog.getBoundingClientRect().right;
        if (edge > window.innerWidth - 12) dialog.style.marginLeft = `${window.innerWidth - 12 - edge}px`;
      };
      fit();
      window.addEventListener("resize", fit);
      const first = grid.querySelector('[role="dialog"] button[data-box-option]');
      if (first) first.focus();
      return () => window.removeEventListener("resize", fit);
    }
    const previous = openedRef.current;
    openedRef.current = "";
    if (!previous) return;
    const cell = grid.querySelector(`button[data-box-key="${previous}"]`);
    if (cell) cell.focus();
  }, [open]);

  const toPlace = placeable.filter(lot => !lot.box);

  // The write selection is the shared hook's (box_allocation.js); this view only
  // closes its open list first, exactly as it did before the extraction.
  function refresh() { setOpen(""); allocation.refresh(); }
  function choose(box, lot) {
    if (locked || !data) return;
    setOpen("");
    allocation.choose(box, lot);
  }

  // Tab reaches every box; the arrow keys step through them in range order, so
  // the grid is navigable without a pointer.
  function stepBoxes(event) {
    const steps = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    const step = steps[event.key];
    if (!step || !gridRef.current) return;
    const cells = [...gridRef.current.querySelectorAll("button[data-box-cell]")];
    const next = cells[cells.indexOf(event.target) + step];
    if (cells.indexOf(event.target) < 0 || !next) return;
    event.preventDefault();
    next.focus();
  }

  function pickerKeys(event) {
    if (event.key === "Escape") { event.stopPropagation(); setOpen(""); return; }
    const steps = { ArrowDown: 1, ArrowUp: -1 };
    const step = steps[event.key];
    if (!step) return;
    const options = [...event.currentTarget.querySelectorAll("button[data-box-option]")];
    const index = options.indexOf(event.target);
    const next = options[index < 0 ? 0 : index + step];
    if (!next) return;
    event.preventDefault();
    next.focus();
  }

  function picker(box, sitting) {
    const key = boxKey(box);
    // A withdrawn horse may leave its box but never enter another, so a swap
    // that would send it to the chosen horse's box is not offered; the swap
    // Action refuses it too (workflows/box_assignments.py).
    const options = placeable
      .filter(lot => lot.id !== sitting?.id && boxKey(lot.box) !== key)
      .filter(lot => !(sitting?.withdrawn && lot.box))
      .sort((first, second) => Number(Boolean(first.box)) - Number(Boolean(second.box)));
    const offering = sitting ? data.canSwap === true : true;
    return <div className="box-picker" role="dialog" aria-label={`Choose a horse for box ${boxLabel(box)}`} onKeyDown={pickerKeys}>
      <div className="box-picker-head">
        <span className="caption">Box {boxLabel(box)} · {sitting ? `Lot ${sitting.number}` : "empty"}</span>
        <strong>{sitting ? "Put a different horse here?" : "Which horse goes in here?"}</strong>
      </div>
      {sitting && <button type="button" className="box-option" data-box-option onClick={() => choose(box, null)}>Take Lot {sitting.number} out of this box</button>}
      {offering && <>
        {options.map(lot => <button key={lot.id} type="button" className="box-option" data-box-option onClick={() => choose(box, lot)}>
          <LotIdentity lot={lot}/>
          <span className="box-option-location muted">{!lot.box ? "Not in a box" : sitting ? `Swaps with box ${boxLabel(lot.box)}` : `Moves from box ${boxLabel(lot.box)}`}</span>
        </button>)}
        {!options.length && <p className="caption box-picker-empty">No other horse can go in this box.</p>}
      </>}
      {sitting && !offering && <p className="caption box-picker-empty">Swapping two horses is not part of your access.</p>}
      <div className="box-picker-foot"><button type="button" className="btn sm" onClick={() => setOpen("")}>Cancel</button></div>
    </div>;
  }

  function cell(box) {
    const key = boxKey(box);
    const sitting = occupants.get(key) || null;
    const inside = <>
      <span className="box-tag">{boxLabel(box)}</span>
      {sitting && !box.feedRoom ? <LotIdentity lot={sitting}/>
        : <span className="box-lot">{box.feedRoom ? "Feed room" : "Add a horse"}</span>}
    </>;
    if (box.feedRoom) return <div key={key} className="box-cell feed">{inside}</div>;
    if (locked) return <div key={key} className={`box-cell ${sitting ? "filled" : "empty"}`}>{inside}</div>;
    return <div key={key} className="box-cell-wrap" onKeyDown={event => { if (event.key === "Escape") setOpen(""); }}>
      <button type="button" data-box-cell data-box-key={key}
        className={`box-cell ${sitting ? "filled" : "empty"}${open === key ? " open" : ""}`}
        aria-label={`Box ${boxLabel(box)}, ${sitting ? `Lot ${sitting.number}` : "empty"}`}
        aria-expanded={open === key}
        onClick={() => setOpen(open === key ? "" : key)}>
        {inside}
      </button>
      {open === key && picker(box, sitting)}
    </div>;
  }

  return <Card title="Horse-to-box allocation" subtitle={`${data?.vendorName || vendor} · ${sale}`}>
    <p className="muted">Tap a box to put a horse in it, change it, or take it out. This saves the allocation for everyone; camera playback is separate.</p>
    <div className="row" style={{ gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
      <button className="btn sm" disabled={busy || loading || Boolean(pending)} onClick={refresh}>Refresh boxes</button>
      {pending && <button className="btn sm primary" disabled={busy} onClick={() => allocation.send(pending)}>Retry same save</button>}
      {data && <span>{placeable.length - toPlace.length} of {placeable.length} horses are in a box</span>}
    </div>
    <p role="status" aria-live="polite" className="muted">{notice}</p>
    {error && <p role="alert" className="error-text">{error}</p>}
    {loading && <p role="status">Loading box allocations…</p>}
    {data && <div ref={gridRef} onKeyDown={stepBoxes}>
      {toPlace.length > 0 && <p className="caption">Still to place: {toPlace.map(lot => `Lot ${lot.number}`).join(", ")}</p>}
      {runs(boxes).map(run => <section key={`${run.barn}:${run.from}`} className="box-run">
        <h4>{run.from === run.to ? `Stable ${run.barn}${run.from}` : `Stables ${run.barn}${run.from} – ${run.barn}${run.to}`}</h4>
        <div className="box-grid">{run.items.map(cell)}</div>
      </section>)}
      {!boxes.length && <p>No assignable range is available in the reviewed import.</p>}
      <p className="caption">Barn K feed rooms 5 and 60 cannot hold horses. Other barns use the published API range; their physical restrictions have not been independently verified.</p>
    </div>}
  </Card>;
}
